/**
 * ШТРИХКОДЫ
 *
 * Дисконтная карта без штрихкода — это просто запись о том, что карта
 * когда-то была. Смысл раздела в том, чтобы на кассе поднести телефон
 * к сканеру, поэтому рисовать код приложение обязано само.
 *
 * Рисуем на месте, своим кодом: картинка со штрихкодом, запрошенная
 * у стороннего сервиса, — это и запрос за пределы страны, и номер твоей
 * карты, ушедший неизвестно кому. Ни первое, ни второе недопустимо.
 *
 * Поддерживаются EAN-13, EAN-8, CODE128 и CODE39 — ими закодировано
 * подавляющее большинство карт. QR рисуется отдельной проверенной
 * библиотекой, которая лежит в приложении, а не запрашивается снаружи.
 */

/** Полоски как последовательность ширин: чёрная, белая, чёрная… */
export type Bars = { widths: number[]; startsBlack: boolean }

/* CODE128 ------------------------------------------------------------------ */

const CODE128_PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312',
  '132212', '221213', '221312', '231212', '112232', '122132', '122231', '113222',
  '123122', '123221', '223211', '221132', '221231', '213212', '223112', '312131',
  '311222', '321122', '321221', '312212', '322112', '322211', '212123', '212321',
  '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121',
  '313121', '211331', '231131', '213113', '213311', '213131', '311123', '311321',
  '331121', '312113', '312311', '332111', '314111', '221411', '431111', '111224',
  '111422', '121124', '121421', '141122', '141221', '112214', '112412', '122114',
  '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112',
  '421211', '212141', '214121', '412121', '111143', '111341', '131141', '114113',
  '114311', '411113', '411311', '113141', '114131', '311141', '411131', '211412',
  '211214', '211232', '233111',
]

const CODE128_STOP = '2331112'

/**
 * Кодируем в наборе B: в нём есть и цифры, и латинские буквы.
 * Набор C вдвое короче на длинных числах, но в дисконтных картах
 * выигрыш незаметен, а ошибиться в переключении наборов легко.
 */
function code128(value: string): Bars | null {
  const codes: number[] = []

  for (const char of value) {
    const point = char.charCodeAt(0)
    // Набор B покрывает символы с пробела по «тильду»
    if (point < 32 || point > 126) return null
    codes.push(point - 32)
  }

  if (codes.length === 0) return null

  const START_B = 104
  let checksum = START_B
  codes.forEach((code, index) => {
    checksum += code * (index + 1)
  })

  const pattern =
    (CODE128_PATTERNS[START_B] ?? '') +
    codes.map((code) => CODE128_PATTERNS[code] ?? '').join('') +
    (CODE128_PATTERNS[checksum % 103] ?? '') +
    CODE128_STOP

  return { widths: [...pattern].map(Number), startsBlack: true }
}

/* CODE39 ------------------------------------------------------------------- */

const CODE39: Record<string, string> = {
  '0': 'nnnwwnwnn', '1': 'wnnwnnnnw', '2': 'nnwwnnnnw', '3': 'wnwwnnnnn',
  '4': 'nnnwwnnnw', '5': 'wnnwwnnnn', '6': 'nnwwwnnnn', '7': 'nnnwnnwnw',
  '8': 'wnnwnnwnn', '9': 'nnwwnnwnn', A: 'wnnnnwnnw', B: 'nnwnnwnnw',
  C: 'wnwnnwnnn', D: 'nnnnwwnnw', E: 'wnnnwwnnn', F: 'nnwnwwnnn',
  G: 'nnnnnwwnw', H: 'wnnnnwwnn', I: 'nnwnnwwnn', J: 'nnnnwwwnn',
  K: 'wnnnnnnww', L: 'nnwnnnnww', M: 'wnwnnnnwn', N: 'nnnnwnnww',
  O: 'wnnnwnnwn', P: 'nnwnwnnwn', Q: 'nnnnnnwww', R: 'wnnnnnwwn',
  S: 'nnwnnnwwn', T: 'nnnnwnwwn', U: 'wwnnnnnnw', V: 'nwwnnnnnw',
  W: 'wwwnnnnnn', X: 'nwnnwnnnw', Y: 'wwnnwnnnn', Z: 'nwwnwnnnn',
  '-': 'nwnnnnwnw', '.': 'wwnnnnwnn', ' ': 'nwwnnnwnn', '*': 'nwnnwnwnn',
}

function code39(value: string): Bars | null {
  const text = `*${value.toUpperCase()}*`
  const widths: number[] = []

  for (const [index, char] of [...text].entries()) {
    const pattern = CODE39[char]
    if (!pattern) return null

    for (const mark of pattern) widths.push(mark === 'w' ? 3 : 1)
    // Между символами — узкий пробел
    if (index < text.length - 1) widths.push(1)
  }

  return { widths, startsBlack: true }
}

/* EAN-13 и EAN-8 ----------------------------------------------------------- */

const EAN_LEFT_ODD = [
  '0001101', '0011001', '0010011', '0111101', '0100011',
  '0110001', '0101111', '0111011', '0110111', '0001011',
]
const EAN_LEFT_EVEN = [
  '0100111', '0110011', '0011011', '0100001', '0011101',
  '0111001', '0000101', '0010001', '0001001', '0010111',
]
const EAN_RIGHT = [
  '1110010', '1100110', '1101100', '1000010', '1011100',
  '1001110', '1010000', '1000100', '1001000', '1110100',
]

/** Какая половина цифр слева кодируется «чётным» набором. */
const EAN_PARITY = [
  'OOOOOO', 'OOEOEE', 'OOEEOE', 'OOEEEO', 'OEOOEE',
  'OEEOOE', 'OEEEOO', 'OEOEOE', 'OEOEEO', 'OEEOEO',
]

/** Контрольная цифра: по ней сканер и понимает, что прочёл верно. */
export function eanChecksum(digits: string): number {
  let sum = 0
  // Считаем справа налево: так одна формула годится и для 8, и для 13 цифр
  for (const [index, char] of [...digits].reverse().entries()) {
    sum += Number(char) * (index % 2 === 0 ? 3 : 1)
  }
  return (10 - (sum % 10)) % 10
}

function ean(value: string): Bars | null {
  const digits = value.replace(/\D/g, '')
  const expected = digits.length === 12 || digits.length === 13 ? 13 : 8

  let full = digits
  // Тринадцатую цифру можно не набирать: она вычисляется
  if (digits.length === expected - 1) full = digits + eanChecksum(digits)
  if (full.length !== expected) return null
  if (eanChecksum(full.slice(0, -1)) !== Number(full.at(-1))) return null

  let bits = '101'

  if (expected === 13) {
    const parity = EAN_PARITY[Number(full[0])] ?? ''
    for (let i = 1; i <= 6; i += 1) {
      const digit = Number(full[i])
      bits += parity[i - 1] === 'E' ? EAN_LEFT_EVEN[digit] : EAN_LEFT_ODD[digit]
    }
    bits += '01010'
    for (let i = 7; i <= 12; i += 1) bits += EAN_RIGHT[Number(full[i])]
  } else {
    for (let i = 0; i < 4; i += 1) bits += EAN_LEFT_ODD[Number(full[i])]
    bits += '01010'
    for (let i = 4; i < 8; i += 1) bits += EAN_RIGHT[Number(full[i])]
  }

  bits += '101'

  // Переводим биты в ширины подряд идущих одинаковых полос
  const widths: number[] = []
  let run = 0
  let current = bits[0]

  for (const bit of bits) {
    if (bit === current) {
      run += 1
    } else {
      widths.push(run)
      current = bit
      run = 1
    }
  }
  widths.push(run)

  return { widths, startsBlack: bits[0] === '1' }
}

/* Общая часть -------------------------------------------------------------- */

export type BarcodeFormat =
  | 'QR'
  | 'EAN13'
  | 'EAN8'
  | 'CODE128'
  | 'CODE39'
  | 'PDF417'
  | 'AZTEC'
  | 'DATA_MATRIX'
  | 'NONE'

/**
 * Полоски для формата. `null` означает «нарисовать нечем» — тогда
 * показываем номер крупными цифрами: на кассе его наберут руками,
 * и это лучше, чем пустое место.
 */
export function barsFor(value: string, format: BarcodeFormat): Bars | null {
  const clean = value.trim()
  if (!clean) return null

  switch (format) {
    case 'EAN13':
    case 'EAN8':
      return ean(clean)
    case 'CODE39':
      return code39(clean)
    case 'CODE128':
      return code128(clean)
    default:
      // Незнакомый или двумерный формат — не наше дело
      return null
  }
}

/** Угадываем формат по самому номеру — человек его обычно не знает. */
export function guessFormat(value: string): BarcodeFormat {
  const clean = value.trim()
  const digits = clean.replace(/\D/g, '')

  if (digits.length === clean.length && (digits.length === 13 || digits.length === 12)) {
    return 'EAN13'
  }
  if (digits.length === clean.length && (digits.length === 8 || digits.length === 7)) {
    return 'EAN8'
  }
  if (/^[\x20-\x7e]+$/.test(clean)) return 'CODE128'
  return 'QR'
}

/** Двумерные коды рисуются как сетка чёрных клеток. */
export const isMatrix = (format: BarcodeFormat): boolean =>
  format === 'QR' || format === 'AZTEC' || format === 'DATA_MATRIX' || format === 'PDF417'
